import React, { useRef } from "react";
import { Box } from "@material-ui/core";
import { Select } from "antd";
import CustomDatePicker from "../../../common-components/CustomDatePicker";
import { AddCommas, convertValuesFM } from "../../../shared-resources/utils";
import { PaginationData } from "../../../common-components/PaginationData";
import DeleteForeverIcon from "@material-ui/icons/DeleteForeverOutlined";
import ColumnPicker from "./ColumnPicker";

// `meritIncPct` is only ever included in this list — filtered out by RenderLabor itself
// (via `showMeritIncPct`) unless the API response actually carried the field (see
// INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §2 — it's present only while viewing the
// currently-editable budget version, omitted entirely everywhere else). `departmentName`
// prefers each row's own `item.department` (present on the API response) and only falls
// back to the page-level `departmentName` prop (the currently-selected department filter)
// for rows that happen not to carry it.
const IDENTITY_COLUMNS = [
  { key: "employeeName", label: "Employee Name", width: 220 },
  { key: "employeeId", label: "Employee ID", width: 100 },
  { key: "budgetSalary", label: "Budget Salary", width: 100 },
  { key: "activeSalary", label: "Active Salary", width: 100 },
  { key: "utilPct", label: "Utilization", width: 100 },
  { key: "meritIncPct", label: "Merit Inc %", width: 100, conditional: true },
  { key: "details", label: "Details", width: 115 },
  { key: "startDate", label: "Start Date", width: 130 },
  { key: "jobTitle", label: "Job Title", width: 120 },
  { key: "managerName", label: "Manager", width: 120 },
  { key: "projectId", label: "Project ID", width: 200 },
  { key: "projectName", label: "Project Name", width: 200 },
  { key: "departmentName", label: "Department", width: 150 },
];

const monthKey = (heading) => heading.split("-")[0].toLowerCase();
const headcountKey = (heading) => {
  const key = monthKey(heading);
  return `headcount${key.charAt(0).toUpperCase()}${key.slice(1)}`;
};

// Same vertical-scroll-with-visible-header approach as Fringe.js's stickyTotalCell: the table
// body scrolls inside a capped-height box while its own `<th>`s stay pinned via CSS position:
// sticky, rather than splitting into separate header/body tables the way Fringe does — same
// visible outcome (headers never scroll out of view), much less structural change here.
// `top: "40px"` on the second header row matches the first row's own fixed 40px height so the
// two rows stack without overlapping.
//
// A FIXED pixel height, not a viewport-relative one — it's calibrated to comfortably fit ~10
// rows (80px header + 70px for the two Headcount/Total rows + 10*35px rows + 35px trailing row
// ≈ 535px, rounded up) without a scrollbar. It's applied unconditionally (not just once row
// count crosses some threshold): 10 or fewer rows naturally render shorter than this and show
// no scrollbar at all, while going from 10 to 15+ rows scrolls WITHIN this same height instead
// of the box itself shrinking or jumping to a viewport-dependent size.
const BODY_SCROLL_STYLE = { maxHeight: "560px", overflowY: "auto" };
const STICKY_TH_ROW1 = { position: "sticky", top: 0, zIndex: 2 };
const STICKY_TH_ROW2 = { position: "sticky", top: "40px", zIndex: 2 };
// Both tables' headers happen to total 80px tall (identity table: one 80px CSS-default row;
// months table: two explicit 40px rows) — pins the Headcount/Total summary rows directly below
// whichever header sits above them, so scrolling through a long employee list never hides the
// header OR these rows (the reason they were pulled to the top of the grid in the first place).
const STICKY_TOTALS_ROW1 = { position: "sticky", top: "80px", zIndex: 1, background: "#fff" };
const STICKY_TOTALS_ROW2 = { position: "sticky", top: "115px", zIndex: 1, background: "#fff" };
// The "+ Add New" row is pinned to the BOTTOM of the scrollable area the same way the
// Headcount/Total rows are pinned to the top — always reachable without scrolling all the way
// down through a long employee list.
const STICKY_BOTTOM_ROW = { position: "sticky", bottom: 0, zIndex: 1, background: "#fff" };

const RenderLabor = ({
  classes,
  laborData,
  months,
  isEdit,
  calculateMonthlySalary,
  editedLaborData,
  handleLaborSalaryChange,
  handleLaborMonthlySalaryChange,
  handleScrollTable,
  LaborRef,
  filteredLabor,
  setFilteredLabor,
  calculateTotalByProject,
  versionStatus,
  handleAddNewLabor,
  onClickLaborDetails,
  handleBudgetSalaryChange,
  handleActiveSalaryChange,
  handleUtilChange,
  handleMeritIncPctChange,
  handleJobTitleChange,
  handleStartDateChange,
  handleProjectIdChange,
  projectOptions,
  selectedYear,
  dateFormat,
  onDeleteIconClick,
  isEditable,
  isEditableBudget,
  departmentName,
  laborTotals,
  showMeritIncPct,
  hiddenColumns,
  onToggleColumn,
  onResetColumns,
}) => {
  // Conditional (Merit Inc %) filtering happens first — the picker itself should only ever
  // offer columns that are actually applicable to the version being viewed; user-hidden
  // columns are filtered separately just below, for the columns actually rendered.
  const pickerColumns = IDENTITY_COLUMNS.filter(
    (col) => !col.conditional || showMeritIncPct,
  );
  const columns = pickerColumns.filter(
    (col) => !hiddenColumns.includes(col.key),
  );

  // One visible vertical scrollbar, not two — same effect Fringe.js gets from its
  // leftBodyScroll/rightBodyScroll pair: both panes stay independently scrollable (so the
  // browser's native wheel/touch/drag handling keeps working on either one), but their scroll
  // position is kept in sync here, and the left pane's own scrollbar is hidden via
  // classes.hiddenScrollbar, so only the right pane's scrollbar shows and the two tables
  // always appear to scroll together.
  //
  // Synced by RATIO (fraction of max scroll), not by copying the raw scrollTop pixel value.
  // The two tables are laid out independently (different column content, different cell
  // widths) and can end up with slightly different total scrollHeights even with the exact
  // same row count — copying scrollTop directly would then drift further apart the closer you
  // get to the bottom, since each pane clamps to its OWN max scrollTop; one side would hit its
  // end before the other and they'd visibly fall out of alignment. Syncing by ratio guarantees
  // both panes always reach 0% and 100% together, regardless of any such height difference.
  const leftPaneRef = useRef(null);
  const syncScrollByRatio = (source, target) => {
    if (!source || !target) return;
    const sourceMax = source.scrollHeight - source.clientHeight;
    const targetMax = target.scrollHeight - target.clientHeight;
    if (sourceMax <= 0 || targetMax <= 0) return;
    target.scrollTop = (source.scrollTop / sourceMax) * targetMax;
  };
  const syncVerticalFromLeft = () =>
    syncScrollByRatio(leftPaneRef.current, LaborRef.current);
  const syncVerticalFromRight = () =>
    syncScrollByRatio(LaborRef.current, leftPaneRef.current);

  const renderIdentityCell = (col, item, index) => {
    switch (col.key) {
      case "employeeName":
        return item.employeeName;
      case "employeeId":
        return item.employeeId;
      case "budgetSalary":
        // Never a direct input — it's always derived, either server-side (budget version:
        // activeSalary × (1 + meritIncPct / 100)) or as-is on every other version. While
        // editing a budget-version row, show the local live preview computed alongside
        // Active Salary/Merit Inc % (applyBudgetSalaryAndForecast in IndirectSummaryTab.js)
        // instead of the last-fetched value, so the two stay visibly in sync as you type.
        return convertValuesFM(
          isEdit && editedLaborData[index]?.meritIncPct !== undefined
            ? editedLaborData[index].budgetSalary
            : item.budgetSalary,
        );
      case "activeSalary":
        return isEdit && !item.employeeId ? (
          <div style={{ paddingRight: "14px" }}>
            <input
              type="text"
              className={classes.inputStyle}
              style={{ width: "100%", boxSizing: "border-box" }}
              value={AddCommas(editedLaborData[index]?.activeSalary)}
              onChange={(e) => {
                const input = e.target;
                const cursorPos = input.selectionStart;
                const oldValue = input.value;
                const rawValue = oldValue.replace(/,/g, "");
                const commasBefore = (
                  oldValue.slice(0, cursorPos).match(/,/g) || []
                ).length;
                const newFormatted = String(
                  AddCommas(rawValue === "" ? 0 : rawValue),
                );
                const commasAfter = (
                  newFormatted.slice(0, cursorPos).match(/,/g) || []
                ).length;
                const newCursorPos = cursorPos + (commasAfter - commasBefore);
                handleActiveSalaryChange(index, rawValue);
                requestAnimationFrame(() => {
                  input.setSelectionRange(newCursorPos, newCursorPos);
                });
              }}
            />
          </div>
        ) : (
          <div style={{ paddingRight: "18px" }}>
            {convertValuesFM(item.activeSalary)}
          </div>
        );
      case "utilPct":
        return isEdit ? (
          <input
            type="number"
            className={classes.inputStyle}
            value={editedLaborData[index]?.utilPct || 0}
            onChange={(e) => handleUtilChange(index, e.target.value)}
          />
        ) : (
          `${item.utilPct}%`
        );
      case "meritIncPct":
        // No global/bulk-apply control, no TBD-row restriction — editable per row for every
        // row exactly like utilPct above (guide §2: "matches how utilPct already works").
        return isEdit ? (
          <input
            type="number"
            className={classes.inputStyle}
            value={editedLaborData[index]?.meritIncPct ?? 0}
            onChange={(e) => handleMeritIncPctChange(index, e.target.value)}
          />
        ) : (
          `${item.meritIncPct ?? 0}%`
        );
      case "details":
        return (
          (Array.isArray(item.listOfNotes) ||
            (item.listOfNotes !== null && item.listOfNotes !== undefined)) && (
            <span
              style={{
                fontWeight: 600,
                color: "#09659c",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => onClickLaborDetails(item)}
            >
              View
            </span>
          )
        );
      case "startDate":
        // TBD rows only (no employeeId), same gating as jobTitle/activeSalary below — real
        // employee rows keep their Costpoint-sourced start date read-only. Same CustomDatePicker
        // component the "+ Add New" dialog's own Start Date field uses, for a consistent picker
        // UI. maxDate caps it to Dec 31 of the selected Year dropdown value, so only that year
        // and earlier years are pickable — future years stay disabled in the picker itself.
        return isEdit && !item.employeeId ? (
          <CustomDatePicker
            dateFormat={dateFormat}
            dateValue={editedLaborData[index]?.startDate}
            setDateValue={(date) => handleStartDateChange(index, date)}
            maxDate={selectedYear ? `${selectedYear}-12-31` : null}
            itemWidth="100%"
          />
        ) : (
          item.startDate
        );
      case "jobTitle":
        // Guide §2: real employee rows (employeeId set) stay Costpoint-sourced/read-only;
        // only TBD rows (no employeeId) get an editable Job Title, same gating as
        // Active Salary above.
        return isEdit && !item.employeeId ? (
          <input
            type="text"
            className={classes.inputStyle}
            style={{ width: "100%", boxSizing: "border-box" }}
            value={editedLaborData[index]?.jobTitle ?? ""}
            onChange={(e) => handleJobTitleChange(index, e.target.value)}
          />
        ) : (
          item.jobTitle
        );
      case "managerName":
        return item.managerName;
      case "projectId":
        // TBD rows only (no employeeId), same gating as jobTitle/activeSalary/startDate above.
        // The open dropdown list shows each option's full "id-name" label (so it's readable
        // while picking), but optionLabelProp="value" makes the CLOSED, selected display show
        // just the project ID — the name is shown separately in the Project Name column (kept
        // in sync by handleProjectIdChange), not duplicated there too.
        return isEdit && !item.employeeId ? (
          <Select
            showSearch
            mode="single"
            style={{ width: "100%", minWidth: 180 }}
            popupMatchSelectWidth={false}
            placeholder="Search to Select"
            showArrow
            optionFilterProp="children"
            optionLabelProp="value"
            filterOption={(input, option) =>
              (option?.label.toLowerCase() ?? "").includes(
                input.toLowerCase(),
              )
            }
            options={projectOptions}
            value={editedLaborData[index]?.projectId || undefined}
            onChange={(val) => handleProjectIdChange(index, val)}
          />
        ) : (
          item.projectId
        );
      case "projectName":
        // Reflects whatever project was just picked in the Project ID dropdown above, even
        // before save — editedLaborData's projectName is kept in sync by handleProjectIdChange.
        return isEdit && !item.employeeId
          ? editedLaborData[index]?.projectName
          : item.projectName;
      case "departmentName":
        return item.department || departmentName;
      default:
        return null;
    }
  };

  const cellTitle = (col, item) => {
    if (col.key === "details") {
      return Array.isArray(item.listOfNotes)
        ? item.listOfNotes.filter((n) => n && n.trim()).join(", ")
        : item.listOfNotes;
    }
    if (col.key === "meritIncPct") return item.meritIncPct ?? "";
    if (col.key === "departmentName") return item.department || departmentName;
    return item[col.key];
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "16px",
        }}
      >
        <div className={classes.tableTitle}>Labor</div>
        <ColumnPicker
          columns={pickerColumns}
          hidden={hiddenColumns}
          onToggle={onToggleColumn}
          onReset={onResetColumns}
        />
      </div>
      {laborData && laborData.length > 0 ? (
        <div>
          <Box>
            <Box display="flex" flexDirection="row">
              <Box
                ref={leftPaneRef}
                className={classes.hiddenScrollbar}
                onScroll={syncVerticalFromLeft}
                style={{
                  boxShadow: "0px 3px 6px #00000029",
                  maxWidth: "720px",
                  minWidth: "720px",
                  overflowX: "auto",
                  marginRight: "8px",
                  ...BODY_SCROLL_STYLE,
                }}
              >
                <table
                  className="firstTableFin"
                  cellPadding={0}
                  cellSpacing={0}
                  style={{
                    width: "720px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: "24px",
                          ...STICKY_TH_ROW1,
                        }}
                      ></th>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            minWidth: `${col.width}px`,
                            maxWidth: `${col.width}px`,
                            ...STICKY_TH_ROW1,
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ borderRadius: "10px" }}>
                    {/* Present on every GET response, for every version (guide §2's new
                        top-level `totals` key) — rendered at the top of the grid rather than
                        only in the existing bottom footer, so it's visible without scrolling
                        past a long employee list. */}
                    {laborTotals && (
                      <>
                        <tr className="totalRow">
                          <td
                            colSpan={columns.length + 1}
                            style={{ fontWeight: 600, ...STICKY_TOTALS_ROW1 }}
                          >
                            Headcount
                          </td>
                        </tr>
                        <tr className="totalRow">
                          <td
                            colSpan={columns.length + 1}
                            style={{ fontWeight: 600, ...STICKY_TOTALS_ROW2 }}
                          >
                            Total
                          </td>
                        </tr>
                      </>
                    )}
                    {filteredLabor.map((item, i) => {
                      const index = item.originalIndex;

                      return (
                        <tr key={index}>
                          <td
                            style={{
                              minWidth: "24px",
                              maxWidth: "24px",
                            }}
                          >
                            {isEditable && (
                              <DeleteForeverIcon
                                style={{
                                  color: "red",
                                  fontSize: "16px",
                                  cursor: "pointer",
                                  float: "left",
                                }}
                                onClick={() => onDeleteIconClick(item)}
                              />
                            )}
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              style={{
                                minWidth: `${col.width}px`,
                                maxWidth: `${col.width}px`,
                                ...(col.key === "budgetSalary"
                                  ? { paddingRight: "12px" }
                                  : {}),
                              }}
                              title={cellTitle(col, item)}
                              className={
                                col.key === "budgetSalary" ||
                                col.key === "activeSalary"
                                  ? classes.centerAlignedData
                                  : undefined
                              }
                            >
                              {renderIdentityCell(col, item, index)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}

                    <tr>
                      <td colSpan={2} style={STICKY_BOTTOM_ROW}>
                        {!isEdit &&
                          versionStatus &&
                          isEditable &&
                          ((versionStatus.toLowerCase() === "open" &&
                            localStorage.getItem("accessType") === "RW") ||
                            isEditableBudget) && (
                            <span
                              style={{
                                cursor: "pointer",
                                color: "#09659c",
                                fontWeight: 600,
                              }}
                              onClick={() => handleAddNewLabor()}
                            >
                              + Add New
                            </span>
                          )}
                      </td>
                      <td
                        colSpan={columns.length - 1}
                        style={STICKY_BOTTOM_ROW}
                      ></td>
                    </tr>
                  </tbody>
                </table>
              </Box>
              <Box
                ref={LaborRef}
                style={{
                  overflowX: "auto",
                  boxShadow: "0px 3px 6px #00000029",
                  ...BODY_SCROLL_STYLE,
                }}
                onScroll={() => {
                  handleScrollTable("Labor");
                  syncVerticalFromRight();
                }}
              >
                <Box display="flex" flexDirection="row">
                  <Box
                    style={{
                      boxShadow: "0px 3px 6px #00000029",
                      marginRight: "8px",
                    }}
                  >
                    <table
                      className="firstTableFin"
                      cellPadding={0}
                      cellSpacing={0}
                    >
                      <thead>
                        <tr>
                          {months.map((month) => (
                            <th
                              key={month.heading}
                              className="centerAlignedHeaderLeftBorder"
                              style={{
                                minWidth: "90px",
                                maxWidth: "90px",
                                height: "40px",
                                ...STICKY_TH_ROW1,
                              }}
                            >
                              {month.heading}
                            </th>
                          ))}
                          <th
                            rowSpan={2}
                            className="centerAlignedHeaderLeftBorder"
                            style={{
                              minWidth: "90px",
                              maxWidth: "90px",
                              ...STICKY_TH_ROW1,
                            }}
                          >
                            Total
                          </th>
                        </tr>
                        <tr>
                          {months.map((month, i) => (
                            <th
                              key={i}
                              className={
                                month.type === "Actual"
                                  ? "actualsHeaderCell"
                                  : "forecastHeaderCell"
                              }
                              style={{ height: "40px", ...STICKY_TH_ROW2 }}
                            >
                              {month.type === "Actual" ? "Actuals" : "Forecast"}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {laborTotals && (
                          <>
                            <tr className="totalRow">
                              {months.map((month) => {
                                const value =
                                  laborTotals[headcountKey(month.heading)] ?? 0;
                                return (
                                  <td
                                    key={month.heading}
                                    className={classes.centerAlignedData}
                                    title={String(value)}
                                    style={STICKY_TOTALS_ROW1}
                                  >
                                    {value}
                                  </td>
                                );
                              })}
                              {/* Headcount isn't additive across months (the same employee can
                                  count in several months), so there's no meaningful grand total
                                  to show here — left blank rather than a misleading sum. */}
                              <td
                                className={classes.centerAlignedData}
                                style={STICKY_TOTALS_ROW1}
                              />
                            </tr>
                            <tr className="totalRow">
                              {months.map((month) => {
                                const value = laborTotals[monthKey(month.heading)] ?? 0;
                                return (
                                  <td
                                    key={month.heading}
                                    className={classes.centerAlignedData}
                                    title={convertValuesFM(value)}
                                    style={STICKY_TOTALS_ROW2}
                                  >
                                    <strong>{convertValuesFM(value)}</strong>
                                  </td>
                                );
                              })}
                              <td
                                className={classes.centerAlignedData}
                                style={STICKY_TOTALS_ROW2}
                              >
                                <strong>
                                  {convertValuesFM(
                                    months.reduce(
                                      (sum, month) =>
                                        sum +
                                        (Number(
                                          laborTotals[monthKey(month.heading)],
                                        ) || 0),
                                      0,
                                    ),
                                  )}
                                </strong>
                              </td>
                            </tr>
                          </>
                        )}
                        {filteredLabor.map((emp) => {
                          const originalIndex = emp.originalIndex;

                          return (
                            <tr key={emp.Emp_number}>
                              {months.map((month) => (
                                <td
                                  key={month}
                                  className={classes.centerAlignedData}
                                >
                                  {isEdit &&
                                  month.type.toLowerCase() === "forecast" ? (
                                    <input
                                      type="text"
                                      className={classes.inputStyle}
                                      value={AddCommas(
                                        editedLaborData[originalIndex][
                                          month.heading
                                        ],
                                      )}
                                      onChange={(e) => {
                                        const input = e.target;
                                        const cursorPos = input.selectionStart;
                                        const oldValue = input.value;
                                        const rawValue = oldValue.replace(
                                          /,/g,
                                          "",
                                        );
                                        const commasBefore = (
                                          oldValue
                                            .slice(0, cursorPos)
                                            .match(/,/g) || []
                                        ).length;
                                        const newFormatted = String(
                                          AddCommas(
                                            rawValue === "" ? 0 : rawValue,
                                          ),
                                        );
                                        const commasAfter = (
                                          newFormatted
                                            .slice(0, cursorPos)
                                            .match(/,/g) || []
                                        ).length;
                                        const newCursorPos =
                                          cursorPos +
                                          (commasAfter - commasBefore);
                                        handleLaborMonthlySalaryChange(
                                          originalIndex,
                                          month.heading,
                                          rawValue,
                                        );
                                        requestAnimationFrame(() => {
                                          input.setSelectionRange(
                                            newCursorPos,
                                            newCursorPos,
                                          );
                                        });
                                      }}
                                    />
                                  ) : (
                                    convertValuesFM(
                                      calculateMonthlySalary(emp, month),
                                    )
                                  )}
                                </td>
                              ))}
                              <td className={classes.centerAlignedData}>
                                {convertValuesFM(
                                  calculateTotalByProject(originalIndex),
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Empty placeholder, not a totals row — the bottom total values were
                            removed. Kept as a sticky-bottom row purely so this table's row
                            count/height still matches the identity table's own sticky-bottom
                            "+ Add New" row, keeping the two panes visually aligned while
                            scrolled to the bottom. */}
                        <tr>
                          {months.map((month) => (
                            <td
                              key={month}
                              className={classes.centerAlignedData}
                              style={STICKY_BOTTOM_ROW}
                            />
                          ))}
                          <td
                            className={classes.centerAlignedData}
                            style={STICKY_BOTTOM_ROW}
                          />
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>
            </Box>
            <PaginationData
              data={laborData}
              setFilteredData={setFilteredLabor}
              title="Labor"
            />
          </Box>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            fontWeight: 600,
            background: "#fff",
            padding: "24px 0",
          }}
        >
          {!isEdit &&
          isEditable &&
          ((versionStatus.toLowerCase() === "open" &&
            localStorage.getItem("accessType") === "RW") ||
            isEditableBudget) ? (
            <span
              style={{
                cursor: "pointer",
                color: "#09659c",
                fontWeight: 600,
              }}
              onClick={() => handleAddNewLabor()}
            >
              + Add New
            </span>
          ) : (
            "No Data"
          )}
        </div>
      )}
    </>
  );
};
export default RenderLabor;
